import { Box, Checkbox, TextField } from "@mui/material";
import Popover from "@mui/material/Popover";
import dateformat from "dateformat";
import { useCallback, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";
import { Button, Text } from "$components";
import useAuth from "$hooks/useAuth";
import { EDBService } from "$services";
import { DispositionType, PreparationOrderStatus } from "$types";
import { getPreparationOrderStatusLabelByRole } from "$utils/typeConstants/PreparationOrder";

type Props = {
  op: any;
  disabled: boolean;
};

const useFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = (i) => {
    setTimeout(() => {
      (document.querySelector(`#date_${i}`) as any).showPicker();
    }, 0);
  };

  return { htmlElRef, setFocus };
};

function StatusHistoryButton({ op, disabled }: Props) {
  const { token } = useAuth();
  const isCollectedDisposition = op?.shipType === DispositionType.COLLECTED;

  const updateOpStatus = useCallback(
    async (
      statusHistory: Map<PreparationOrderStatus, string>,
      status: number,
      op: any
    ) => {
      const newOP = op;
      const statusHistoryObj = [];

      // Special handling for COLLECTED disposition type
      if (
        op?.shipType === "Collecte" &&
        status === PreparationOrderStatus.COLLECTED
      ) {
        // Automatically add intermediate shipping statuses with the same date as COLLECTED
        const collectedDate = statusHistory.get(
          PreparationOrderStatus.COLLECTED
        );

        // Add all statuses up to COLLECTED
        statusHistory.forEach((statusDate, statusNum) => {
          statusHistoryObj.push({ status: statusNum, statusDate });
        });

        // Add missing shipping statuses if they don't exist
        const shippingStatuses = [
          PreparationOrderStatus.TO_SHIP,
          PreparationOrderStatus.SHIPPED,
          PreparationOrderStatus.DELIVERED,
        ];

        shippingStatuses.forEach((shippingStatus) => {
          const existingStatus = Array.from(statusHistory.keys()).find(
            (s) => s === shippingStatus
          );
          if (!existingStatus) {
            statusHistoryObj.push({
              status: shippingStatus,
              statusDate:
                collectedDate || new Date().toISOString().split("T")[0],
            });
          }
        });
      } else {
        // Normal handling for other disposition types
        statusHistory.forEach((statusDate, index) => {
          statusHistoryObj.push({ status: index, statusDate });
        });
      }

      newOP.status = status;
      await EDBService.EditOPStatus(
        token,
        {
          statusHistoryObj,
          status,
        },
        newOP._id
      );
    },
    [token]
  );

  const { userRole } = useAuth();
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null
  );
  const [selectedStatus, setSelectedStatus] = useState<PreparationOrderStatus>(
    PreparationOrderStatus.IN_PROGRESS
  );
  const [statusOPDateMap, setStatusOPDateMap] = useState<
    Map<PreparationOrderStatus, string>
  >(new Map());
  const classes = useStyles();
  const { htmlElRef: dateRef, setFocus: setDateFocus } = useFocus();

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSelectedStatus(PreparationOrderStatus.IN_PROGRESS);
    setStatusOPDateMap(new Map());
  };

  const handleSavePopover = (o: any) => {
    updateOpStatus(statusOPDateMap, selectedStatus, o);
    setPopoverAnchor(null);
    setSelectedStatus(PreparationOrderStatus.IN_PROGRESS);
    setStatusOPDateMap(new Map());
  };

  const handleOpenPopover = (o: any) => {
    setSelectedStatus(o.status);

    // Build a local copy of statusHistory to avoid mutating the source data
    const localHistory = [...o.statusHistory];
    if (
      o.status === PreparationOrderStatus.IN_PROGRESS &&
      !localHistory.find(
        (status) => status.status === PreparationOrderStatus.IN_PROGRESS
      )
    ) {
      const status0 = localHistory.find(
        (status) => status.status === PreparationOrderStatus.CREATED
      );
      localHistory.push({
        status: PreparationOrderStatus.IN_PROGRESS,
        statusDate: status0 ? status0.statusDate : new Date(),
      });
    }

    // Create a new Map instead of mutating the existing one
    const newMap = new Map<PreparationOrderStatus, string>();
    localHistory.forEach((status) => {
      newMap.set(
        status.status,
        dateformat(new Date(status.statusDate), "yyyy-mm-dd")
      );
    });
    setStatusOPDateMap(newMap);
  };

  const renderStatusPopOverOP = () => {
    const content = [];
    const maxStatus = isCollectedDisposition
      ? PreparationOrderStatus.COLLECTED
      : PreparationOrderStatus.COLLECTED;

    for (let i = PreparationOrderStatus.IN_PROGRESS; i <= maxStatus; i += 1) {
      // Skip shipping statuses for COLLECTED disposition
      if (
        isCollectedDisposition &&
        (i === PreparationOrderStatus.TO_SHIP ||
          i === PreparationOrderStatus.SHIPPED ||
          i === PreparationOrderStatus.DELIVERED)
      ) {
        // eslint-disable-next-line no-continue
        continue;
      }

      content.push(
        <Box
          className={classes.line}
          style={{ justifyContent: "space-between", margin: 15 }}
          key={`status${i}`}
        >
          <Box
            style={{
              paddingRight: 15,
            }}
          >
            {(i === PreparationOrderStatus.PREPARED ||
              (isCollectedDisposition &&
                i === PreparationOrderStatus.COLLECTED)) && (
              <Checkbox
                checked={i <= selectedStatus}
                disabled={
                  isCollectedDisposition &&
                  i === PreparationOrderStatus.COLLECTED
                    ? // For COLLECTED status, only require PREPARED to be completed
                      selectedStatus < PreparationOrderStatus.PREPARED ||
                      !statusOPDateMap.get(PreparationOrderStatus.PREPARED)
                    : // Original logic for PREPARED status
                      selectedStatus !== i &&
                      (selectedStatus + 1 !== i ||
                        Boolean(!statusOPDateMap.get(i - 1)))
                }
                value={i}
                onChange={() => {
                  if (
                    isCollectedDisposition &&
                    i === PreparationOrderStatus.COLLECTED
                  ) {
                    // For COLLECTED, allow direct toggle if PREPARED is completed
                    if (statusOPDateMap.get(PreparationOrderStatus.PREPARED)) {
                      setSelectedStatus(
                        i === selectedStatus
                          ? PreparationOrderStatus.PREPARED
                          : i
                      );
                      if (i !== selectedStatus) {
                        setDateFocus(i);
                      }
                      if (!statusOPDateMap.get(i)) {
                        setStatusOPDateMap(new Map(statusOPDateMap.set(i, "")));
                      }
                    }
                  } else {
                    // Original logic for other statuses
                    setSelectedStatus((prev) => (prev === i ? i - 1 : i));
                    if (i !== selectedStatus) {
                      setDateFocus(i);
                    }
                    if (!statusOPDateMap.get(i)) {
                      setStatusOPDateMap(new Map(statusOPDateMap.set(i, "")));
                    }
                  }
                }}
              />
            )}

            <Text
              key={i}
              style={{
                fontWeight: selectedStatus === i ? "bold" : "normal",
                // Gray out shipping statuses for COLLECTED
                color:
                  isCollectedDisposition &&
                  (i === PreparationOrderStatus.TO_SHIP ||
                    i === PreparationOrderStatus.SHIPPED ||
                    i === PreparationOrderStatus.DELIVERED)
                    ? "#cccccc"
                    : "inherit",
              }}
            >
              {getPreparationOrderStatusLabelByRole(userRole, i)}
            </Text>
          </Box>
          <Box className={classes.inputContainer}>
            <TextField
              id={`date_${i}`}
              inputRef={dateRef}
              type="date"
              variant="outlined"
              key={i}
              required={i < selectedStatus}
              inputProps={{ className: classes.input }}
              className={classes.inputDate}
              onChange={(e: any) => {
                setStatusOPDateMap(
                  new Map(statusOPDateMap.set(i, e.target.value))
                );
              }}
              disabled={
                (i !== PreparationOrderStatus.PREPARED &&
                  !(
                    isCollectedDisposition &&
                    i === PreparationOrderStatus.COLLECTED
                  )) ||
                selectedStatus > i
              }
              value={selectedStatus < i ? "aa" : statusOPDateMap.get(i)}
            />
          </Box>
        </Box>
      );
    }
    return content;
  };

  let validForm = true;

  for (let i = PreparationOrderStatus.CREATED; i <= selectedStatus; i++) {
    // Skip validation for shipping statuses in COLLECTED disposition
    if (
      isCollectedDisposition &&
      (i === PreparationOrderStatus.TO_SHIP ||
        i === PreparationOrderStatus.SHIPPED ||
        i === PreparationOrderStatus.DELIVERED)
    ) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (!statusOPDateMap.get(i)) {
      validForm = false;
      break;
    }
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {op.status !== PreparationOrderStatus.CREATED &&
      op.status !== PreparationOrderStatus.CANCELLED ? (
        <Box>
          <Button
            variant="contained"
            onClick={(e) => {
              handleOpenPopover(op);
              setPopoverAnchor(e.currentTarget);
            }}
            disabled={disabled}
            style={{ width: "103px" }}
          >
            {getPreparationOrderStatusLabelByRole(userRole, op.status)}
          </Button>
          <Popover
            id={op.status}
            open={Boolean(popoverAnchor)}
            anchorEl={popoverAnchor}
            onClose={() => setPopoverAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <Box>
              {renderStatusPopOverOP()}
              <Box className={classes.buttonBar}>
                <Button variant="outlined" onClick={handleClosePopover}>
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSavePopover(op)}
                  disabled={!validForm}
                >
                  Enregistrer
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>
      ) : (
        <Text>{getPreparationOrderStatusLabelByRole(userRole, op.status)}</Text>
      )}
    </>
  );
}

const useStyles = createUseStyles(() => ({
  line: {
    display: "flex",
    alignItems: "center",
  },
  buttonBar: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
    justifyContent: "center",
    marginBottom: 10,
  },
  inputContainer: {
    "& input::-webkit-calendar-picker-indicator": {
      display: "block",
      "-webkit-appearance": "textfield",
    },
  },
  input: {
    paddingTop: `${theme.spacing(1)}px !important`,
    paddingBottom: `${theme.spacing(1)}px !important`,
  },
  inputDate: {
    width: "162px !important",
  },
}));

export default StatusHistoryButton;
